/**
 * Helpers for turning an API rejection into a human-readable message.
 *
 * `extractApiErrorMessage` is the single source of truth for user-facing API
 * error text across the app — inline banners, dialog errors, and any `setError`
 * / `toast.error` that surfaces a failed request. Prefer it over reading
 * `err.message` directly: an `AxiosError`'s `.message` is always the useless
 * boilerplate `"Request failed with status code 400"`, and this helper is
 * careful never to surface that (see #728).
 *
 * The Axios interceptor in `client.ts` already toasts errors globally, but a
 * toast is easy to miss when a dialog stays open. Components that keep a modal
 * on screen after a failed submit should also render the message inline — use
 * this for that too.
 */

import axios from "axios";

interface ValidationErrorItem {
  loc?: (string | number)[];
  msg?: string;
}

/**
 * The backend serialises errors as RFC-7807 problem+json (see the backend's
 * `app/core/exceptions.py`): a business-rule rejection carries the specific
 * message in `detail` (with `title` a generic label like "Bad Request"), while
 * a raw `HTTPException` mirrors the same string into both `detail` and `title`.
 * FastAPI 422s put an array of validation items in `detail`. We read `detail`
 * first, then fall back to `title`.
 */
interface ProblemBody {
  detail?: unknown;
  title?: unknown;
}

const isValidationErrorItem = (value: unknown): value is ValidationErrorItem =>
  typeof value === "object" && value !== null;

const problemBody = (err: unknown): ProblemBody | undefined => {
  if (axios.isAxiosError(err)) {
    return err.response?.data as ProblemBody | undefined;
  }
  // Non-axios rejections that still carry a `response.data` (e.g. re-thrown
  // shapes) are handled structurally.
  return (err as { response?: { data?: ProblemBody } })?.response?.data;
};

/**
 * Plain text for a problem+json `detail`, whatever its shape.
 *
 * `detail` is usually a string, but a structured `HTTPException` detail
 * reaches the client as an object — e.g. the 409 overlap payload from
 * POST /admin/assignments/{id}/assign is `{ message, conflicts: [...] }` (#701).
 * Anything that ends up in a toast or a JSX text node must be a string: handing
 * that object to `toast.error` made React throw "Objects are not valid as a
 * React child" inside the app-level <Toaster>, which unmounted the whole app
 * into a blank page (#926). Returns `undefined` when there is no usable text.
 */
export const detailMessage = (detail: unknown): string | undefined => {
  if (typeof detail === "string") {
    return detail.trim().length > 0 ? detail : undefined;
  }
  if (typeof detail === "object" && detail !== null && !Array.isArray(detail)) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }
  return undefined;
};

/** One overlapping engagement listed in the assign 409 payload. */
export interface OverlapConflict {
  id: number;
  title: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
}

/** The assign 409 payload: the consultant is already booked in the window. */
export interface OverlapConflictDetail {
  message: string;
  conflicts: OverlapConflict[];
}

const optionalString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

/**
 * Recognise the "consultant already has overlapping assignments" 409 from
 * POST /admin/assignments/{id}/assign (backend #701) and normalise it.
 *
 * The backend sends `detail: { message, conflicts: [{ id, title, start_date,
 * end_date, status }] }` inside the RFC 7807 body. This reads it defensively:
 * it tolerates `conflicts` at the top level of the body too, skips malformed
 * entries, and returns `null` for anything that is not a 409 with at least one
 * readable conflict — callers then fall back to the generic error path.
 */
export const extractOverlapConflict = (err: unknown): OverlapConflictDetail | null => {
  const status = axios.isAxiosError(err)
    ? err.response?.status
    : (err as { response?: { status?: unknown } })?.response?.status;
  if (status !== 409) {
    return null;
  }
  const data = problemBody(err) as (ProblemBody & { conflicts?: unknown }) | undefined;
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const detail = data.detail;
  const rawConflicts =
    typeof detail === "object" && detail !== null && !Array.isArray(detail)
      ? (detail as { conflicts?: unknown }).conflicts
      : data.conflicts;
  if (!Array.isArray(rawConflicts)) {
    return null;
  }
  const conflicts: OverlapConflict[] = rawConflicts.flatMap(item => {
    if (typeof item !== "object" || item === null) {
      return [];
    }
    const c = item as Record<string, unknown>;
    if (typeof c.id !== "number") {
      return [];
    }
    return [
      {
        id: c.id,
        title: typeof c.title === "string" && c.title ? c.title : `Assignment #${c.id}`,
        start_date: optionalString(c.start_date),
        end_date: optionalString(c.end_date),
        status: typeof c.status === "string" ? c.status : "",
      },
    ];
  });
  if (conflicts.length === 0) {
    return null;
  }
  const message =
    detailMessage(detail) ??
    (typeof data.title === "string" && data.title ? data.title : undefined) ??
    "The consultant already has overlapping assignments in this period.";
  return { message, conflicts };
};

/**
 * Pull the most specific message available out of an unknown API rejection.
 *
 * Resolution order: `detail` (string, a structured detail's `message`, or a
 * joined 422 validation array) →
 * `title` → a non-Axios `Error.message` → `fallback`. An `AxiosError`'s own
 * `.message` is deliberately never returned — it is always the HTTP boilerplate
 * string and must not reach users (#728).
 */
export const extractApiErrorMessage = (err: unknown, fallback: string): string => {
  const data = problemBody(err);
  const detail = data?.detail;

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map(item => {
        if (!isValidationErrorItem(item)) {
          return String(item);
        }
        const field = item.loc?.join(" → ") || "Field";
        return `${field}: ${item.msg ?? "invalid"}`;
      })
      .join("\n");
  }

  const fromDetail = detailMessage(detail);
  if (fromDetail) {
    return fromDetail;
  }

  const title = data?.title;
  if (typeof title === "string" && title.trim().length > 0) {
    return title;
  }

  // Only a non-Axios Error's message is meaningful (e.g. a hand-thrown
  // `new Error("No active session")`). Never surface an AxiosError's
  // "Request failed with status code N" boilerplate.
  if (!axios.isAxiosError(err) && err instanceof Error && err.message) {
    return err.message;
  }

  return fallback;
};

/**
 * The backend answered 404 or 405: the route is not there (an older release,
 * or a record the caller may not reach). Callers that can degrade to a
 * read-only view use this instead of toasting a failure (#1133).
 */
export const isEndpointMissing = (err: unknown): boolean =>
  axios.isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 405);
